/**
 * Platform theme configuration stored in GlobalSettings (key: platform_theme).
 * Values are HSL components without the hsl() wrapper, e.g. "221 83% 53%".
 */

export const PLATFORM_THEME_SETTING_KEY = 'platform_theme';

/** Phase 1 — admin-editable tokens per color mode */
export type ThemeModeVariables = {
  primary?: string;
  secondary?: string;
  destructive?: string;
  radius?: string;
};

export type PlatformThemeConfig = {
  light: ThemeModeVariables;
  dark: ThemeModeVariables;
};

export type ThemeColorMode = 'light' | 'dark';

/** Maps JSON keys to CSS custom property names in Main.css */
export const THEME_TOKEN_TO_CSS_VAR: Record<keyof ThemeModeVariables, string> = {
  primary: '--primary',
  secondary: '--secondary',
  destructive: '--destructive',
  radius: '--radius',
};

/** Defaults aligned with app/src/client/Main.css :root */
export const DEFAULT_PLATFORM_THEME: PlatformThemeConfig = {
  light: {
    primary: '221 83% 53%',
    secondary: '32 100% 37%',
    destructive: '0 84.2% 60.2%',
    radius: '0.5rem',
  },
  dark: {
    primary: '221 83% 53%',
    secondary: '209 91% 61%',
    destructive: '0 62.8% 63%',
    radius: '0.5rem',
  },
};

const HSL_COMPONENTS_PATTERN = /^\d{1,3}(\.\d+)?\s+\d{1,3}(\.\d+)?%\s+\d{1,3}(\.\d+)?%$/;
const RADIUS_PATTERN = /^\d+(\.\d+)?(rem|px)$/;

export function isValidHslComponents(value: string): boolean {
  return HSL_COMPONENTS_PATTERN.test(value.trim());
}

export function isValidRadius(value: string): boolean {
  return RADIUS_PATTERN.test(value.trim());
}

function sanitizeModeVariables(input: unknown): ThemeModeVariables {
  if (!input || typeof input !== 'object') {
    return {};
  }

  const raw = input as Record<string, unknown>;
  const result: ThemeModeVariables = {};

  if (typeof raw.primary === 'string' && isValidHslComponents(raw.primary)) {
    result.primary = raw.primary.trim();
  }
  if (typeof raw.secondary === 'string' && isValidHslComponents(raw.secondary)) {
    result.secondary = raw.secondary.trim();
  }
  if (typeof raw.destructive === 'string' && isValidHslComponents(raw.destructive)) {
    result.destructive = raw.destructive.trim();
  }
  if (typeof raw.radius === 'string' && isValidRadius(raw.radius)) {
    result.radius = raw.radius.trim();
  }

  return result;
}

/** Parse and validate stored JSON; returns null if missing or invalid */
export function parsePlatformTheme(settingValue: unknown): PlatformThemeConfig | null {
  if (!settingValue || typeof settingValue !== 'object') {
    return null;
  }

  const raw = settingValue as Record<string, unknown>;
  const light = sanitizeModeVariables(raw.light);
  const dark = sanitizeModeVariables(raw.dark);

  if (Object.keys(light).length === 0 && Object.keys(dark).length === 0) {
    return null;
  }

  return { light, dark };
}

/** Merge stored overrides onto defaults for a single mode */
export function resolveThemeForMode(
  theme: PlatformThemeConfig | null,
  mode: ThemeColorMode
): ThemeModeVariables {
  const defaults = DEFAULT_PLATFORM_THEME[mode];
  const overrides = theme?.[mode] ?? {};
  return { ...defaults, ...overrides };
}

export function mergePlatformThemeConfig(
  stored: PlatformThemeConfig | null
): PlatformThemeConfig {
  return {
    light: { ...DEFAULT_PLATFORM_THEME.light, ...stored?.light },
    dark: { ...DEFAULT_PLATFORM_THEME.dark, ...stored?.dark },
  };
}

export function sanitizePlatformThemeConfig(input: unknown): PlatformThemeConfig {
  const parsed = parsePlatformTheme(input);
  return mergePlatformThemeConfig(parsed);
}
